/**
 * Typed failures. Every non-2xx answer from the API is an RFC 9457 problem
 * (05-api.md "Errors"); apps branch on `code`, never on `title` or `detail`.
 */
import {
  problemSchema,
  problemTypeFor,
  type ErrorCode,
  type Problem,
} from '@qrkey/contracts/errors';

export class ApiError extends Error {
  readonly status: number;
  /** Stable code (`INVALID_CREDENTIALS`, `TOTP_REQUIRED`, …). Unknown codes from a newer server pass through. */
  readonly code: ErrorCode | (string & {});
  readonly problem: Problem;
  /** X-Request-Id of the failed request, for support. */
  readonly requestId: string | undefined;

  constructor(problem: Problem, requestId?: string) {
    super(problem.detail);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
    this.requestId = requestId;
  }

  /** `retryAfterSeconds` of ACCOUNT_LOCKED / RATE_LIMITED problems. */
  get retryAfterSeconds(): number | undefined {
    const v = (this.problem as { retryAfterSeconds?: unknown }).retryAfterSeconds;
    return typeof v === 'number' ? v : undefined;
  }

  /** `fields` of VALIDATION_FAILED: messages per dotted path. */
  get fields(): Record<string, string[]> {
    return this.problem.fields ?? {};
  }
}

/** The request never got an answer (offline, DNS, connection refused, CORS). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach QRkey. Check the connection and try again.', { cause });
    this.name = 'NetworkError';
  }
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

const STATUS_CODES: Record<number, ErrorCode> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'PAYLOAD_TOO_LARGE',
  429: 'RATE_LIMITED',
  502: 'BAD_GATEWAY',
  503: 'SERVICE_UNAVAILABLE',
  504: 'GATEWAY_TIMEOUT',
};

/** The problem in a response body, or a synthetic one when the body is not a problem. */
export function toProblem(status: number, body: unknown): Problem {
  const parsed = problemSchema.safeParse(body);
  if (parsed.success) return parsed.data;
  const code: ErrorCode = STATUS_CODES[status] ?? (status >= 500 ? 'INTERNAL' : 'BAD_REQUEST');
  return {
    type: problemTypeFor(code),
    title: 'The request failed',
    status: status >= 400 && status <= 599 ? status : 500,
    code,
    detail: `The server answered ${status}.`,
  };
}

export async function apiErrorFromResponse(response: Response, body?: unknown): Promise<ApiError> {
  let payload = body;
  if (payload === undefined) {
    try {
      const text = await response.clone().text();
      payload = text ? JSON.parse(text) : undefined;
    } catch {
      payload = undefined;
    }
  }
  return new ApiError(
    toProblem(response.status, payload),
    response.headers.get('x-request-id') ?? undefined,
  );
}
